<template>
  <UiDrawer
    :model-value="modelValue"
    :side="side"
    :width="drawerWidth"
    :close-on-backdrop="true"
    :close-on-esc="true"
    animation="slide-fade"
    :border-radius="drawerBorderRadius"
    @update:model-value="handleUpdate"
  >
    <template #header>
      <div class="flex justify-end items-center py-2 px-5">
        <button
          class="flex items-center justify-center w-8 h-8 bg-transparent border-none cursor-pointer text-black transition-opacity duration-200 p-0 hover:opacity-70"
          @click="handleClose"
          aria-label="Close"
        >
          <PhX :size="24" weight="regular" />
        </button>
      </div>
    </template>

    <div class="flex flex-col h-full overflow-y-auto p-0">
      <div class="flex flex-col items-start gap-3 px-5 py-4 border-b border-border">
        <div
          class="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white font-semibold text-base flex-shrink-0"
        >
          {{ userInitials }}
        </div>
        <div class="flex flex-col min-w-0 flex-1">
          <h3 class="text-sm font-semibold text-text-primary truncate">{{ fullUserName }}</h3>
          <p v-if="userEmail" class="text-sm text-text-secondary truncate">{{ userEmail }}</p>
          <p v-else-if="userMobile" class="text-sm text-text-secondary truncate">
            {{ userMobile }}
          </p>
        </div>
      </div>

      <nav class="flex flex-col p-0 gap-0">
        <button
          class="flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10"
          @click="navigateTo(profileRoute)"
        >
          <PhUser :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">My Profile</span>
        </button>

        <button
          class="relative flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10"
          @click="navigateTo(myOrdersRoute)"
        >
          <PhPackage :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">My Orders</span>
          <span
            v-if="ordersCount > 0"
            class="ml-auto bg-warning text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
          >
            {{ ordersCount > 9 ? '9+' : ordersCount }}
          </span>
        </button>

        <button
          class="relative flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10"
          @click="navigateTo(myReservationsRoute)"
        >
          <PhCalendarCheck :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">My Reservations</span>
          <span
            v-if="reservationsCount > 0"
            class="ml-auto bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
          >
            {{ reservationsCount > 9 ? '9+' : reservationsCount }}
          </span>
        </button>

        <button
          class="relative flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10"
          @click="handleNotificationsClick"
        >
          <PhBell :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">Notifications</span>
          <span
            v-if="notificationsCount > 0"
            class="ml-auto bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center min-w-[20px]"
          >
            {{ notificationsCount > 9 ? '9+' : notificationsCount }}
          </span>
        </button>

        <a
          href="#"
          class="flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10"
          @click.prevent="handlePartnerClick"
        >
          <PhHandshake :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">Partner with us</span>
        </a>

        <button
          class="flex items-center gap-4 py-4 px-5 text-primary no-underline bg-transparent border-none cursor-pointer transition-colors duration-200 text-base font-normal w-full text-left hover:bg-primary/5 active:bg-primary/10 mt-auto"
          @click="handleLogout"
        >
          <PhSignOut :size="24" weight="regular" class="text-primary flex-shrink-0" />
          <span class="text-primary text-base font-normal">Sign Out</span>
        </button>
      </nav>
    </div>
  </UiDrawer>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppFlow } from '@/composables/core/useAppFlow'
import UiDrawer from '@/components/common/UiDrawer.vue'
import {
  PhX,
  PhUser,
  PhPackage,
  PhCalendarCheck,
  PhBell,
  PhHandshake,
  PhSignOut,
} from '@phosphor-icons/vue'

interface Props {
  modelValue: boolean
  side?: 'left' | 'right' | 'top' | 'bottom'
  variant?: 'back' | 'close'
}

withDefaults(defineProps<Props>(), {
  side: 'right',
  variant: 'close',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  logout: []
}>()

const router = useRouter()
const authStore = useAuthStore()
const { getContextualRoute } = useAppFlow()

const ordersCount = ref(0)
const reservationsCount = ref(0)
const notificationsCount = ref(0)

const fullUserName = computed(() => authStore.fullUserName)
const userEmail = computed(() => authStore.user?.email || null)
const userMobile = computed(() => authStore.user?.mobile_no || null)

const userInitials = computed(() => {
  if (!authStore.user) return 'U'
  const firstName = authStore.user.first_name || ''
  const lastName = authStore.user.last_name || ''
  const firstInitial = firstName.charAt(0).toUpperCase()
  const lastInitial = lastName.charAt(0).toUpperCase()
  return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || 'U'
})

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

const drawerWidth = computed(() => {
  return windowWidth.value < 768 ? '100vw' : '293px'
})

const drawerBorderRadius = computed(() => {
  return windowWidth.value < 768 ? '0' : '1rem 0 0 1rem'
})

const profileRoute = computed(() => getContextualRoute('/profile'))
const myOrdersRoute = computed(() => getContextualRoute('/my-orders'))
const myReservationsRoute = computed(() => getContextualRoute('/my-reservations'))

const handleResize = (): void => {
  windowWidth.value = window.innerWidth
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

const handleUpdate = (value: boolean): void => {
  emit('update:modelValue', value)
}

const handleClose = (): void => {
  emit('update:modelValue', false)
}

const handlePartnerClick = (): void => {
  void navigateTo('/restaurant-signup')
}

const handleNotificationsClick = (): void => {
  handleClose()
}

const navigateTo = async (path: string): Promise<void> => {
  await router.push(path)
  handleClose()
}

const handleLogout = async (): Promise<void> => {
  emit('logout')
  handleClose()
}
</script>
